import { Component, inject, input, linkedSignal, model, output } from '@angular/core';
import { form, FormField, FormValueControl, required } from '@angular/forms/signals';
import {
  HttpError,
  SupplierContractCheckItemRequest,
  SupplierContractCheckItemResult,
} from '../../../../core/clients/omni-core/models';
import {
  SwitchBar,
  SwitchBarOption,
  InputLabel,
  Dropzone,
  DropzoneFile,
  Textarea,
  DetectBrowserDirective,
  TooltipDirective,
} from 'excellence-ui';
import { NgIcon } from '@ng-icons/core';
import { toModel, toPayload } from './definitions';
import { FieldErrors } from '../../../../shared/components/field-errors/field-errors';
import { Api } from '../../../../core/clients/omni-core/api';
import { uploadFile } from '../../../../core/clients/omni-core/functions';
import { getFileUrl } from '../../../organizations/pipes/organization-file-url-pipe';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-supplier-contract-check-item-form-field',
  imports: [
    SwitchBar,
    SwitchBarOption,
    NgIcon,
    FormField,
    InputLabel,
    FieldErrors,
    Dropzone,
    DropzoneFile,
    Textarea,
    TranslocoPipe,
    NgClass,
    DetectBrowserDirective,
    TooltipDirective,
  ],
  templateUrl: './supplier-contract-check-item-form-field.html',
  styleUrl: './supplier-contract-check-item-form-field.css',
})
export class SupplierContractCheckItemFormField implements FormValueControl<SupplierContractCheckItemRequest | null> {
  private api = inject(Api);

  organizationId = input.required<string>();
  readonly value = model<SupplierContractCheckItemRequest | null>(null);
  readonly fieldValueChange = output<SupplierContractCheckItemRequest>();

  protected formModel = linkedSignal(() => toModel(this.value()));
  protected form = form(this.formModel, (schemaPath) => {
    required(schemaPath.explanation, {
      when: (ctx) => {
        const result = ctx.valueOf(schemaPath.result);
        return result === 'ALERT' || result === 'INFO';
      },
    });
  });

  protected fileUpload = linkedSignal<{
    isUploading: boolean;
    uploadError: HttpError | unknown | null;
    progress: number;
    file: File | { url: string } | null;
  }>(() => {
    const value = this.value();
    return {
      isUploading: false,
      uploadError: null,
      progress: value?.documentId ? 100 : 0,
      file: value?.documentId ? { url: getFileUrl(value.documentId, this.organizationId()) } : null,
    };
  });

  private getPayload() {
    return {
      ...toPayload(this.formModel()),
      documentId: this.value()?.documentId,
    };
  }

  protected onSwitchChange(value: SupplierContractCheckItemResult) {
    const payload = {
      ...this.getPayload(),
      result: value,
    };
    this.value.set(payload);
    this.fieldValueChange.emit(payload);
  }

  protected onExplanationChange(value: string) {
    const payload = {
      ...this.getPayload(),
      explanation: value,
    };
    this.value.set(payload);
    this.fieldValueChange.emit(payload);
  }

  protected onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (files && files.length > 0) {
      this.onFileDrop(files);
    }
  }

  protected async onFileDrop(files: FileList) {
    const firstFile = files[0];
    if (!firstFile) {
      return;
    }
    this.fileUpload.set({
      isUploading: true,
      uploadError: null,
      progress: 10,
      file: firstFile,
    });
    try {
      // upload file
      const { id } = await this.api.invoke(uploadFile, {
        body: firstFile,
        'X-Filename': encodeURIComponent(firstFile.name),
        oid: this.organizationId(),
      });
      this.fileUpload.set({
        isUploading: false,
        uploadError: null,
        progress: 100,
        file: { url: getFileUrl(id, this.organizationId()), name: firstFile.name },
      });
      this.onDocumentIdChange(id);
    } catch (error) {
      this.fileUpload.update((value) => ({
        ...value,
        isUploading: false,
        uploadError: error,
      }));
    }
  }

  private onDocumentIdChange(documentId: string) {
    const payload = {
      ...toPayload(this.formModel()),
      documentId,
    };
    this.value.set(payload);
    this.fieldValueChange.emit(payload);
  }

  markAsTouched() {
    this.form.document().markAsTouched();
    this.form.explanation().markAsTouched();
    this.form.result().markAsTouched();
  }
}
